import { Categories } from '../constants/Pages';
import { environment } from '../config/environment';
import { getGroupSlugByGame } from '../constants/ShareLeaderboard';

const DEFAULT_CHALLENGE_POINTS = '300';

type GameFeedContent = {
  meta: {
    locale: string,
    name: string,
    description: string,
    rules: string,
    alias: string,
    htmlTitle: string,
    htmlDescription: string,
    categories: string[],
    details: string | null,
    fullTitle: string | null,
    subTitle: string | null,
    isFlex: string | null,
    backgroundColor: string | null,
    hasCollection: string | null,
    aspectRatio: string | null,
    isGRM: boolean | null,

    thumbs: {
      promoThumbPath: string,
      thumbPath: string,
      secondaryThumbPath: string,
      screenshotPath: string,
      squareIcon76: string,
      squareIcon120: string,
      squareIcon152: string,
      squareIcon167: string,
      squareIcon180: string,
      squareIcon192: string,
      squareIcon512: string,
      pwaIcon192: string,
      promoTile384x216: string,
      promoTile280x280: string,
      pwaIcon152: string,
      pwaIcon180: string,
      promoTile1000x312: string,
      promoTile760x312: string,
      pwaIcon512: string,
      pwaIcon76: string,
      pwaIcon167: string,
      pwaIcon120: string,
      webGameAreaTopAssetLeft: string,
      promoTile280x600: string,

    }
  };
  [gameProp: string]: any;
};

function hasCategory(meta: Game, category) {
  const lowercaseCategory = category.toLowerCase(); //The new game api categories are in lower case
  return meta.categories.some(cat => cat.toLowerCase() === lowercaseCategory);
}

export class Game {
  public static Parse(
    { meta, ...gameData }: GameFeedContent,
    seoFeed
  ): Game {
    const game = new Game({});
    game.id = gameData.id;
    game.name = gameData.name;
    game.slug = gameData.slug;
    game.version = gameData.version;
    game.renderingType = gameData.renderingType;
    game.sdkName = gameData.sdkName;
    game.thumbPath = meta.thumbs.thumbPath;
    game.secondaryThumbPath = meta.thumbs.secondaryThumbPath;
    game.promoThumbPath = meta.thumbs.promoThumbPath;
    game.description = meta.description;
    game.isGRM = meta.isGRM || false;
    game.screenshotPath = meta.thumbs.screenshotPath;

    game.categories = meta.categories || [];
    game.membersOnly = hasCategory(game, Categories.Members);

    const seo = seoFeed.find(
      (seoItem) => seoItem && seoItem.Arena5Slug === gameData.slug
    );

    if (seo && seo.URLAlias) {
      game.seo_GameName = seo.GameName;
      game.seo_URLAlias = seo.URLAlias;
      game.seo_FinalURL = seo.FinalURL;
      game.seo_OnPageTitle = seo.OnPageTitle;
      game.seo_OnPageDescription = seo.OnPageDescription;
      game.seo_MetaHTMLTitle = seo.MetaHTMLTitle;
      game.seo_MetaHTMLDescription = seo.MetaHTMLDescription;
      game.seo_IsInnerHtmlOnPageDescription = seo.IsInnerHtmlOnPageDescription;
      game.seo_IsHidden = seo.IsHidden;
      game.seo_IsFakeGame = seo.IsFakeGame;
      game.seo_IsFriendGame = seo.IsFriendGame;
      game.seo_IsSupportRewardVideo = seo.IsSupportRewardVideo;
      game.seo_IsRewardInModal = seo.IsRewardInModal;
      game.seo_adUnitCustom = seo.adUnitCustom;
      game.seo_isScreenshot = seo.IsScreenshot;
      game.seo_topPlayerScores = seo.topPlayerScores;
      game.seo_OnPageTitleInnerHtml = seo.OnPageTitleInnerHtml;
      game.seo_PromoDescription = seo.PromoDescription;
      game.seo_TheaterMode = seo.TheaterMode;
      game.seo_PreviewDateToShow = seo.PreviewDateToShow;
      game.seo_IsSchemaEnable = seo.IsSchemaEnable;
      game.seo_OgTitle = seo.OgTitle;
      game.seo_OgDescription = seo.OgDescription;
      game.seo_PreventGameEndOnSavedScore = seo.PreventGameEndOnSavedScore;
      game.seo_IsChallengeGame = seo.IsChallengeGame;
      game.seo_HideLeaderboard = seo.HideLeaderboard;
      //There's no need to differentiate fake games now since the FakeGames.json follows the new api structure
      game.assetOriginUrl = `${environment.AARP_GAMES_BLOB}/${gameData.sdkName}/${gameData.version}/`;
    }

    game.groupSlug = getGroupSlugByGame(game.slug);

    return game;
  }

  public static getOnPageTitle(game: Game) {
    return game.seo_OnPageTitle || game.name;
  }

  public static getURLAlias(game: Game) {
    return `/games/${game.seo_URLAlias}`;
  }
  public id: string;
  public slug: string;
  public name: string;
  public sdkName: string;
  public version: string;
  public categories: string[];
  public thumbPath: string;
  public promoThumbPath: string;
  public secondaryThumbPath: string;
  public description: string;
  public isGRM: boolean;
  public screenshotPath: string;
  public membersOnly: boolean = false;
  public assetOriginUrl: string;
  public groupSlug: string;
  public seo_GameName: string;
  public seo_URLAlias: string;
  public seo_FinalURL: string;
  public seo_OnPageTitle: string;
  public seo_OnPageDescription: string;
  public seo_MetaHTMLTitle: string;
  public seo_MetaHTMLDescription: string;
  public seo_IsInnerHtmlOnPageDescription?: boolean;
  public seo_IsHidden?: boolean;
  public seo_IsFakeGame?: boolean;
  public seo_IsFriendGame?: boolean;
  public seo_IsSupportRewardVideo?: boolean;
  public seo_IsRewardInModal?: boolean;
  public seo_adUnitCustom?: string;
  public seo_isScreenshot?: boolean;
  public seo_OnPageTitleInnerHtml?: string;
  public seo_PromoDescription?: string;
  public seo_TheaterMode?: boolean;
  public seo_PreviewDateToShow?: string;
  public seo_IsSchemaEnable?: boolean;
  public seo_OgTitle?: string;
  public seo_OgDescription?: string;
  public seo_PreventGameEndOnSavedScore?: boolean;
  public seo_IsChallengeGame?: boolean;
  public seo_ChallengePoints?: string;
  public seo_topPlayerScores?: string[];
  public seo_HideLeaderboard?: boolean;
  public renderingType: string;

  constructor(data) {
    this.id = data.id;
    this.slug = data.slug;
    this.name = data.name;
    this.sdkName = data.sdkName;
    this.version = data.version;
    this.categories = data.categories || [];
    this.thumbPath = data.thumbPath;
    this.promoThumbPath = data.promoThumbPath;
    this.secondaryThumbPath = data.secondaryThumbPath;
    this.description = data.description;
    this.isGRM = data.isGRM || false;
    this.screenshotPath = data.screenshotPath;
    this.assetOriginUrl = data.assetOriginUrl;
    this.membersOnly = hasCategory(this, Categories.Members);
    this.seo_GameName = data.seo_GameName || data.name;
    this.seo_URLAlias = data.seo_URLAlias;
    this.seo_FinalURL = data.seo_FinalURL;
    this.seo_OnPageTitle = data.seo_OnPageTitle;
    this.seo_OnPageDescription = data.seo_OnPageDescription;
    this.seo_MetaHTMLTitle = data.seo_MetaHTMLTitle;
    this.seo_MetaHTMLDescription = data.seo_MetaHTMLDescription;
    this.seo_IsInnerHtmlOnPageDescription =
      data.seo_IsInnerHtmlOnPageDescription;
    this.seo_IsHidden = data.seo_IsHidden;
    this.seo_IsFakeGame = data.seo_IsFakeGame;
    this.seo_IsFriendGame = data.seo_IsFriendGame;
    this.seo_IsSupportRewardVideo = data.seo_IsSupportRewardVideo;
    this.seo_IsRewardInModal = data.seo_IsRewardInModal;
    this.seo_adUnitCustom = data.seo_adUnitCustom;
    this.seo_isScreenshot = data.seo_isScreenshot;
    this.seo_OnPageTitleInnerHtml = data.seo_OnPageTitleInnerHtml;
    this.seo_PromoDescription = data.seo_PromoDescription;
    this.seo_TheaterMode = data.seo_TheaterMode;
    this.seo_PreviewDateToShow = data.seo_PreviewDateToShow;
    this.seo_IsSchemaEnable = data.seo_IsSchemaEnable;
    this.seo_OgTitle = data.seo_OgTitle;
    this.seo_OgDescription = data.seo_OgDescription;
    this.seo_PreventGameEndOnSavedScore = data.seo_PreventGameEndOnSavedScore;
    this.seo_IsChallengeGame = data.seo_IsChallengeGame;
    this.seo_ChallengePoints = data.seo_ChallengePoints || DEFAULT_CHALLENGE_POINTS;
    this.seo_topPlayerScores = data.seo_topPlayerScores || [];
    this.seo_HideLeaderboard = data.seo_HideLeaderboard || false;
    this.renderingType = data.renderingType;

    this.groupSlug = getGroupSlugByGame(this.slug);
  }

  public isNew() {
    return hasCategory(this, Categories.New);
  }

  public isSpain() {
    return hasCategory(this, Categories.Juegos);
  }

  public isStayingSharp() {
    return hasCategory(this, Categories.StayingSharp);
  }

  public updateAssetsOriginURL() {
    if (!this.sdkName || !this.version) {
      throw new Error('Game object has insufficient data to build assets url');
    }

    let path = this.renderingType === 'JavaScript' ? this.sdkName : this.slug;

    this.assetOriginUrl =
      environment.AARP_GAMES_BLOB +
      '/' +
      path +
      '/' +
      this.version +
      '/';
  }
}
